import React from 'react';
import { defineMessage, FormattedMessage } from 'react-intl';
import type {
  PaylinkAmountType,
  PaylinkAmountWithCurrency,
} from '@packages/gql/generated/shopping/graphql';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import { CurrencyInput } from './CurrencyInput';

const message = defineMessage({
  id: 'Paylink.CurrencyInput.textFieldLabel',
  defaultMessage: 'Wunschbetrag in Euro',
});
const FixedAmount: React.FC<{ amount: number | null; currencyCode: string | null }> = ({
  amount,
  currencyCode,
}) => {
  const format = useFormatPrice();
  return (
    <div className="text-size-body-sm flex items-center justify-between">
      <div>
        <FormattedMessage
          key="Payment.Paylink.Prepare.Current-text"
          id="Payment.Paylink.Prepare.Current"
          defaultMessage="aktuell"
        />
        :
      </div>
      <strong>{format.formatPrice(amount || 0, currencyCode || 'EUR')}</strong>
    </div>
  );
};

const VariableAmount: React.FC<{
  initialAmount?: number | null;
  minAmount?: number | null;
  maxAmount?: number | null;
  currencyCode?: string | null;
  onChange: (value: number) => void;
}> = ({ initialAmount, minAmount, maxAmount, currencyCode, onChange }) => (
  <>
    <FixedAmount amount={initialAmount || null} currencyCode={currencyCode || null} />
    <div className="border-b-color-grey my-1 border-b" />
    <CurrencyInput
      overrideLabel={message}
      initialAmount={initialAmount || null}
      minAmount={minAmount || null}
      maxAmount={maxAmount || null}
      currencyCode={currencyCode || null}
      onChange={onChange}
    />
  </>
);

const AnyAmount: React.FC<{
  initialAmount?: number | null;
  currencyCode?: string | null;
  onChange: (value: number) => void;
}> = ({ initialAmount, currencyCode, onChange }) => (
  <CurrencyInput
    overrideLabel={message}
    initialAmount={initialAmount || 100}
    minAmount={100}
    maxAmount={null}
    currencyCode={currencyCode || null}
    onChange={onChange}
  />
);

export const EnterPaymentAmount: React.FC<{
  amount: PaylinkAmountWithCurrency | null;
  amountType: PaylinkAmountType | null;
  onChange: (newAmount: number) => void;
}> = ({ amount, amountType, onChange }) => {
  switch (amountType) {
    case 'FIXED':
      return (
        <FixedAmount amount={amount?.value || null} currencyCode={amount?.currencyCode || null} />
      );
    case 'MINIMUM':
      return (
        <VariableAmount
          initialAmount={amount?.value}
          minAmount={amount?.value}
          currencyCode={amount?.currencyCode}
          onChange={onChange}
        />
      );
    case 'RECOMMENDATION':
      return (
        <VariableAmount
          initialAmount={amount?.value}
          maxAmount={amount?.value}
          currencyCode={amount?.currencyCode}
          onChange={onChange}
        />
      );
    case 'ANY':
      return (
        <AnyAmount
          initialAmount={amount?.value}
          currencyCode={amount?.currencyCode}
          onChange={onChange}
        />
      );
    default:
      return (
        <FixedAmount amount={amount?.value || null} currencyCode={amount?.currencyCode || null} />
      );
  }
};
